import { CaretDownIcon, LinkSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import {
  AlertDialog,
  Button,
  DropdownMenu,
  Flex,
  Text,
} from "@radix-ui/themes";

import { groupAccentCssVar } from "../workspace/groupColors";
import type { GroupColorSlot } from "../workspace/workspace.types";
import { getGroupLetter } from "../workspace/workspace.utils";
import { GroupRowPresentation } from "./GroupRow.pres";
import type { GroupSelectControllerReturn } from "./GroupSelect.controller";
import { ColorDot } from "./groupSelectRow.parts";
import { MenuRowPresentation } from "./MenuRow.pres";
import "./groupSelect.css";

export interface GroupSelectPresentationProps
  extends GroupSelectControllerReturn {}

const GroupLinkIcon = ({ colorSlot }: { colorSlot?: GroupColorSlot }) => (
  <LinkSimpleIcon
    size={12}
    weight="bold"
    aria-hidden
    data-testid="group-link-icon"
    className="shrink-0"
    style={colorSlot ? { color: groupAccentCssVar(colorSlot) } : undefined}
  />
);

/**
 * Radix `DropdownMenu` for assigning a widget to a lettered symbol group.
 * Existing global groups appear as rows (including zero local members).
 * Letters are assigned only on create (lowest free A–Z).
 */
export const GroupSelectPresentation = ({
  activeGroup,
  rows,
  canCreate,
  colorOptions,
  noneLabel = "None",
  unassignedAriaLabel = "No group assigned",
  open,
  noneHovered,
  pendingDeleteId,
  pendingDeleteCount,
  isSelectingForDelete,
  selectedGroupIds,
  allGroupsSelected,
  pendingBulkDelete,
  pendingBulkDeleteCount,
  onOpenChange,
  onNoneHoverChange,
  onSelectNone,
  onSelectGroup,
  onCreateGroup,
  onStartDeleteGroups,
  onCancelDeleteGroups,
  onToggleGroupSelected,
  onToggleSelectAll,
  onRequestBulkDelete,
  onCancelBulkDeleteConfirm,
  onConfirmBulkDelete,
  onGroupHoverChange,
  onStartNameEdit,
  onDraftNameChange,
  onCommitName,
  onCancelNameEdit,
  onChangeGroupColor,
  onRequestDelete,
  onSingleDeleteOpenChange,
  onConfirmSingleDelete,
}: GroupSelectPresentationProps) => {
  const isEditing = rows.some((row) => row.isEditing);

  return (
    <span data-menu-root className="inline-flex">
      <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
        <DropdownMenu.Trigger>
          <Button
            type="button"
            variant="ghost"
            color="gray"
            size="1"
            className="group/group-select"
            aria-label={
              activeGroup
                ? `Group ${getGroupLetter(activeGroup.id)}`
                : unassignedAriaLabel
            }
          >
            {activeGroup ? (
              <>
                <GroupLinkIcon colorSlot={activeGroup.colorSlot} />
                <Text size="1" className="atd-numeric">
                  {getGroupLetter(activeGroup.id)}
                </Text>
              </>
            ) : (
              <GroupLinkIcon />
            )}
            <CaretDownIcon
              size={12}
              aria-hidden
              className="invisible group-hover/group-select:visible group-focus-visible/group-select:visible"
            />
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          align="end"
          size="1"
          variant="soft"
          onCloseAutoFocus={(event) => {
            if (isEditing) {
              event.preventDefault();
            }
          }}
        >
          {isSelectingForDelete && rows.length > 0 && (
            <Flex align="center" px="2" py="1">
              <Button
                type="button"
                variant="ghost"
                color="gray"
                size="1"
                onPointerDown={(event) => event.preventDefault()}
                onClick={onToggleSelectAll}
              >
                {allGroupsSelected ? "Deselect all" : "Select all"}
              </Button>
            </Flex>
          )}

          <MenuRowPresentation
            isActive={activeGroup === null}
            isHovered={noneHovered}
            label={noneLabel}
            onHoverChange={onNoneHoverChange}
            onSelect={onSelectNone}
          >
            <Flex align="center" gap="4" className="group-select-dot-letter">
              <ColorDot hollow />
              <span aria-hidden className="group-select-letter-slot" />
            </Flex>
            <Text size="1">{noneLabel}</Text>
          </MenuRowPresentation>

          {rows.map((row) => (
            <GroupRowPresentation
              key={row.group.id}
              row={row}
              colorOptions={colorOptions}
              onHoverChange={(hovered) =>
                onGroupHoverChange(row.group.id, hovered)
              }
              onSelect={() => {
                if (isSelectingForDelete) {
                  onToggleGroupSelected(row.group.id);
                  return;
                }
                onSelectGroup(row.group.id);
              }}
              onStartNameEdit={() => onStartNameEdit(row.group.id)}
              onDraftNameChange={(value) =>
                onDraftNameChange(row.group.id, value)
              }
              onCommitName={() => onCommitName(row.group.id)}
              onCancelNameEdit={() => onCancelNameEdit(row.group.id)}
              onChangeGroupColor={(slot) =>
                onChangeGroupColor(row.group.id, slot)
              }
              onRequestDelete={() => onRequestDelete(row.group.id)}
            />
          ))}

          <DropdownMenu.Separator />

          <Flex align="center" justify="between" gap="2" mt="1">
            <DropdownMenu.Item
              disabled={!canCreate}
              title={canCreate ? undefined : "Maximum of 26 groups"}
              onSelect={(event) => {
                event.preventDefault();
                onCreateGroup();
              }}
              className="group-select-create-item"
            >
              <PlusIcon size={13} weight="bold" aria-hidden />
              Create group
            </DropdownMenu.Item>
            <Flex align="center" gap="1" flexShrink="0">
              {isSelectingForDelete ? (
                <>
                  {selectedGroupIds.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      color="red"
                      size="1"
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={onRequestBulkDelete}
                    >
                      {`Delete ${selectedGroupIds.length} ${selectedGroupIds.length === 1 ? "Group" : "Groups"}`}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    color="gray"
                    size="1"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={onCancelDeleteGroups}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                rows.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    color="gray"
                    size="1"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={onStartDeleteGroups}
                  >
                    Edit
                  </Button>
                )
              )}
            </Flex>
          </Flex>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <AlertDialog.Root
        open={pendingDeleteId !== null}
        onOpenChange={onSingleDeleteOpenChange}
      >
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>
            Delete Group{" "}
            {pendingDeleteId !== null ? getGroupLetter(pendingDeleteId) : ""}?
          </AlertDialog.Title>
          <AlertDialog.Description size="2">
            {pendingDeleteCount > 0
              ? `${pendingDeleteCount} widget${pendingDeleteCount === 1 ? "" : "s"} will be unassigned. This cannot be undone.`
              : "This cannot be undone."}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button type="button" variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button type="button" color="red" onClick={onConfirmSingleDelete}>
                Delete
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <AlertDialog.Root
        open={pendingBulkDelete}
        onOpenChange={(dialogOpen) => {
          if (!dialogOpen) {
            onCancelBulkDeleteConfirm();
          }
        }}
      >
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>
            Are you sure you want to delete these groups?
          </AlertDialog.Title>
          <AlertDialog.Description size="2">
            {pendingBulkDeleteCount > 0
              ? `${pendingBulkDeleteCount} widget${pendingBulkDeleteCount === 1 ? "" : "s"} will be unassigned. This cannot be undone.`
              : "This cannot be undone."}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button type="button" variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button type="button" color="red" onClick={onConfirmBulkDelete}>
                Delete
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </span>
  );
};
